import {
  CloseOutlined,
  EditOutlined,
  ExpandAltOutlined,
  ShrinkOutlined,
} from "@ant-design/icons";
import {
  Button,
  Flex,
  Image,
  Modal,
  Space,
  Tag,
  theme,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { TInterest } from "../../../models/interests.model";
import { TPost } from "../../../models/posts.model";
import { TResponse } from "../../../services/apiWorker";
import { getMediaFile } from "../../../services/media.service";
import { formatDate } from "../../../utils/date";
import { hexToRgba } from "../../../utils/hexToRgba";
import { Editor } from "../../editor/Editor";
import { PImage } from "../../PImage";

const { Title, Paragraph, Text } = Typography;
const { useToken } = theme;

interface ModalPreviewEventProps {
  open: boolean;
  onClose: () => void;
  onAddToTimeline?: () => void;
  onEdit?: () => void;
  event: TPost | null;
  interestsData?: TResponse<TInterest>;
}

function ModalPreviewEvent({
  open,
  onClose,
  onAddToTimeline,
  onEdit,
  event,
  interestsData,
}: ModalPreviewEventProps) {
  const { token } = useToken();
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const previousImageUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    previousImageUrlsRef.current = imageUrls;
  });

  useEffect(() => {
    let isMounted = true;
    const currentRunBlobUrls: string[] = [];
    const urlsToRevoke = previousImageUrlsRef.current;

    if (urlsToRevoke && urlsToRevoke.length > 0) {
      urlsToRevoke.forEach((url) => {
        if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
      });
    }

    if (event?.medias && event.medias.length > 0) {
      setLoadingImages(true);
      const mediaIds = event.medias.filter(
        (id): id is string => typeof id === "string" && id.length > 0
      );
      if (mediaIds.length === 0) {
        if (isMounted) {
          setLoadingImages(false);
          setImageUrls([]);
        }
        return;
      }
      const urlPromises = mediaIds.map((mediaId) => {
        return new Promise<string | null>((resolve) => {
          getMediaFile({
            mediaId: mediaId,
            onSuccess: (data: Blob) => {
              if (!isMounted) {
                resolve(null);
                return;
              }
              resolve(URL.createObjectURL(data));
            },
            onError: (error: any) => {
              console.error("Error fetching media file:", mediaId, error);
              resolve(null);
            },
          });
        });
      });

      Promise.all(urlPromises).then((results) => {
        if (isMounted) {
          const validUrls = results.filter(
            (url): url is string => url !== null
          );
          setImageUrls(validUrls);
          setLoadingImages(false);
        }
      });
    } else {
      if (isMounted) {
        setImageUrls([]);
        setLoadingImages(false);
      }
    }

    return () => {
      isMounted = false;
      currentRunBlobUrls.forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [event?.id]);

  useEffect(() => {
    return () => {
      const finalUrlsToRevoke = previousImageUrlsRef.current;
      finalUrlsToRevoke.forEach((url) => {
        if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
      });
    };
  }, []);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  useEffect(() => {
    if (!open) {
      setIsExpanded(false);
    }
  }, [open]);

  if (!event) {
    return null;
  }

  const eventDateStr = formatDate(new Date(event.eventDate || event.createdAt));
  const isToday =
    new Date(event.eventDate || event.createdAt).toDateString() ===
    new Date().toDateString();
  const displayDate = isToday ? `Today, ${eventDateStr}` : eventDateStr;
  const eventInterests = event.interests
    ?.map((id) => interestsData?.content.find((i) => i.id === id))
    .filter((i): i is TInterest => !!i);

  let lexicalJSONString: string | null = null;
  let plainTextContent: string | null = null;
  if (event.content) {
    const contentSource =
      typeof event.content === "object" && event.content !== null
        ? event.content.text
        : event.content;
    if (typeof contentSource === "string") {
      const potentialJSON = contentSource.trim();
      if (potentialJSON.startsWith('{"root":') && potentialJSON.endsWith("}")) {
        try {
          JSON.parse(potentialJSON);
          lexicalJSONString = potentialJSON;
        } catch (e) {
          console.warn("Content looked like JSON but failed to parse:", e);
          plainTextContent = contentSource;
        }
      } else {
        plainTextContent = contentSource;
      }
    } else {
      console.warn("Invalid content format:", typeof contentSource);
      plainTextContent = "[Invalid Content Format]";
    }
  }

  const initialModalHeight = "85vh";
  const expandedModalHeight = "95vh";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered={!isExpanded}
      width={isExpanded ? "95%" : "70%"}
      closable={false}
      style={{ top: isExpanded ? token.marginLG : undefined }}
      styles={{
        content: {
          backgroundColor: "rgba(39, 39, 42, 0.80)",
          backdropFilter: "blur(20px)",
          padding: 0,
          height: isExpanded ? expandedModalHeight : initialModalHeight,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
        mask: {
          backdropFilter: "none",
        },
      }}
      bodyStyle={{
        padding: 0,
        overflow: "hidden", // Changed overflowY to hidden, inner div handles scroll
        width: "100%",
        flex: 1,
        display: "flex", // Make body a flex container too
        flexDirection: "column", // Stack header and content area
      }}
      destroyOnClose
    >
      {/* Header Section */}
      <Flex
        justify="flex-end"
        align="center"
        style={{
          // Takes full width relative to body's padding=0
          gap: token.marginSM,
          // Padding applied directly to the header Flex
          padding: `${token.paddingSM}px ${token.paddingContentHorizontalLG}px`,
          flexShrink: 0, // Prevent header from shrinking
          backgroundColor: "rgba(0,0,0,0.1)", // User's background choice
          borderBottom: `1px solid ${token.colorSplit}`, // User's border choice
          // No negative margins needed as body padding is 0
        }}
      >
        {onEdit && (
          <Button onClick={onEdit} icon={<EditOutlined />}>
            Edit Post
          </Button>
        )}
        <Button type="primary">Save</Button>
        <Tooltip title={isExpanded ? "Shrink" : "Expand"}>
          <Button
            onClick={toggleExpand}
            icon={isExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
            aria-label={isExpanded ? "Shrink modal" : "Expand modal"}
          />
        </Tooltip>
        <Tooltip title="Close">
          <Button
            onClick={onClose}
            icon={<CloseOutlined />}
            aria-label="Close modal"
          />
        </Tooltip>
      </Flex>

      {/* Scrollable Content Section */}
      <div
        style={{
          flex: 1, // Takes remaining vertical space
          overflowY: "auto", // Only this div scrolls
          // Apply the padding that was previously on the body wrapper div
          padding: `${token.paddingContentVerticalLG}px ${token.paddingContentHorizontalLG}px`,
          paddingTop: token.margin, // Add some top padding below the header
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Text
            type="secondary"
            style={{
              textAlign: "center",
              display: "block",
              fontSize: token.fontSizeHeading5,
            }}
          >
            Published {displayDate}
          </Text>

          {event.title && (
            <Title
              level={3}
              style={{
                textAlign: "center",
                marginBottom: token.marginXS,
                color: token.colorText,
                fontSize: token.fontSizeHeading1,
              }}
            >
              {event.title}
            </Title>
          )}

          {eventInterests && eventInterests.length > 0 && (
            <div style={{ textAlign: "center", marginBottom: token.margin }}>
              {eventInterests.map((interest) => (
                <Tag
                  key={interest.id}
                  style={{
                    backgroundColor:
                      hexToRgba(interest.color, 0.2) || interest.color,
                    color: interest.color,
                    border: `1px solid ${interest.color}`,
                  }}
                >
                  {interest.name}
                </Tag>
              ))}
            </div>
          )}

          {lexicalJSONString && (
            <Flex
              justify="center"
              style={{ width: "100%", marginTop: token.margin }}
            >
              <div
                style={{
                  maxWidth: "65ch",
                  width: "100%",
                  color: token.colorText,
                }}
              >
                <Editor
                  state={lexicalJSONString}
                  onState={() => {}}
                  styles={{
                    editorContainer: {
                      backgroundColor: "transparent",
                    },
                    editorInner: {
                      backdropFilter: "none",
                    },
                  }}
                  maxLength={undefined}
                />
              </div>
            </Flex>
          )}

          {plainTextContent && !lexicalJSONString && (
            <Flex
              justify="center"
              style={{ width: "100%", marginTop: token.margin }}
            >
              <Paragraph
                style={{
                  textAlign: "left",
                  maxWidth: "65ch",
                  width: "100%",
                  color: token.colorText,
                }}
              >
                {plainTextContent}
              </Paragraph>
            </Flex>
          )}

          <Flex
            justify="center"
            align="center"
            style={{
              width: "100%",
              minHeight: loadingImages ? "100px" : "auto",
              marginTop: token.margin,
              marginBottom: token.marginLG,
            }}
          >
            <Image.PreviewGroup>
              <Flex
                wrap="wrap"
                gap={token.marginSM}
                justify="center"
                style={{ width: "100%" }}
              >
                {event.medias
                  .filter((mediaId) => mediaId)
                  .map((mediaId) => {
                    return (
                      <PImage
                        key={mediaId}
                        mediaId={mediaId}
                        style={{
                          maxHeight: "500px",
                          objectFit: "contain",
                        }}
                      />
                    );
                  })}
              </Flex>
            </Image.PreviewGroup>
          </Flex>
        </Space>
      </div>
    </Modal>
  );
}

export default ModalPreviewEvent;
